/// <reference lib="dom" />
/**
 * Browser → storage multipart upload with per-part checkpoints. The server
 * owns the upload (creates it, presigns parts, completes it); this only PUTs
 * the parts and remembers which landed. Byte-accurate progress: the stream is
 * counted as the browser pulls it.
 */

import {
  checkpointMatches,
  completedBytes,
  completionManifest,
  pendingParts,
  planParts,
  type CompletedPart,
  type CompletedParts,
  type UploadCheckpoint,
} from './resumable.js';

export type UploadPhase = 'compressing' | 'uploading' | 'resuming' | 'completing';

export interface UploadProgress {
  phase: UploadPhase;
  /** 0..1 within this artifact. */
  fraction: number;
  sentBytes: number;
  totalBytes: number;
}

export class UploadError extends Error {
  constructor(message: string, readonly reason: 'auth' | 'network' | 'server' | 'gone') {
    super(message);
    this.name = 'UploadError';
  }
}

export interface CheckpointStore {
  get(key: string): Promise<UploadCheckpoint | null>;
  put(checkpoint: UploadCheckpoint): Promise<void>;
  delete(key: string): Promise<void>;
}

export const noCheckpoints: CheckpointStore = {
  get: async () => null,
  put: async () => {},
  delete: async () => {},
};

/**
 * One PUT of a Blob: a part of a multipart upload, or a whole small file. The body is sent as is, not streamed: Chrome
 * streams a request body only over HTTP/2 or 3, and S3's presigned URLs speak HTTP/1.1 (a streamed PUT fails there
 * with ERR_ALPN_NEGOTIATION_FAILED). Progress is reported when the PUT completes; parts are a few MB each.
 */
export async function putBody(
  url: string,
  body: Blob,
  headers: Record<string, string>,
  onBytes: (sent: number) => void,
  signal?: AbortSignal,
): Promise<Response> {
  try {
    const resp = await fetch(url, { method: 'PUT', headers, body, signal });
    if (resp.ok) onBytes(body.size);
    return resp;
  } catch (err) {
    if (signal?.aborted) throw err;
    throw new UploadError(`Network error: ${(err as Error).message}`, 'network');
  }
}

export interface MultipartUploadOptions {
  /** Checkpoint key: what is being uploaded (a recording id, a file fingerprint). */
  key: string;
  ownerId: string;
  body: Blob;
  contentType: string;
  uploadId: string;
  partSizeBytes: number;
  /** Presigned PUT URLs for these part numbers. */
  partUrls(partNumbers: number[]): Promise<Record<number, string>>;
  complete(parts: CompletedPart[]): Promise<void>;
  store?: CheckpointStore;
  onProgress?: (progress: UploadProgress) => void;
  signal?: AbortSignal;
}

/** How many parts to presign at once. Keeps the URL list short-lived. */
const PART_URL_BATCH = 8;

export async function uploadMultipart(options: MultipartUploadOptions): Promise<void> {
  const store = options.store ?? noCheckpoints;
  const totalBytes = options.body.size;
  const plan = planParts(totalBytes, options.partSizeBytes);
  const stored = await store.get(options.key).catch(() => null);
  const resumable =
    checkpointMatches(stored, options.ownerId, totalBytes) &&
    stored.uploadId === options.uploadId &&
    stored.partSizeBytes === options.partSizeBytes;
  const completed: CompletedParts = resumable ? { ...stored.completed } : {};
  const report = (phase: UploadPhase, inFlight = 0) => {
    const sentBytes = Math.min(totalBytes, completedBytes(plan, completed) + inFlight);
    options.onProgress?.({ phase, fraction: totalBytes === 0 ? 1 : sentBytes / totalBytes, sentBytes, totalBytes });
  };
  report(resumable ? 'resuming' : 'uploading');

  const remaining = pendingParts(plan, completed);
  for (let i = 0; i < remaining.length; i += PART_URL_BATCH) {
    const batch = remaining.slice(i, i + PART_URL_BATCH);
    const urls = await options.partUrls(batch.map((p) => p.partNumber));
    for (const part of batch) {
      const url = urls[part.partNumber];
      if (!url) throw new UploadError(`No presigned URL for part ${part.partNumber}`, 'server');
      const slice = options.body.slice(part.start, part.end, options.contentType);
      const resp = await putBody(url, slice, {}, (sent) => report('uploading', sent), options.signal);
      if (!resp.ok) throw new UploadError(`Part ${part.partNumber} failed (${resp.status})`, 'server');
      // S3 quotes the ETag; the completion manifest wants it verbatim.
      const etag = resp.headers.get('ETag');
      if (!etag) throw new UploadError(`Part ${part.partNumber} returned no ETag (is ETag exposed by the bucket CORS?)`, 'server');
      completed[part.partNumber] = etag;
      // Checkpoint AFTER the part lands: an attempt killed on the next byte resumes here.
      await store
        .put({
          key: options.key,
          ownerId: options.ownerId,
          uploadId: options.uploadId,
          totalBytes,
          partSizeBytes: options.partSizeBytes,
          completed,
          updatedAt: Date.now(),
        })
        .catch(() => {});
      report('uploading');
    }
  }

  report('completing');
  await options.complete(completionManifest(plan, completed));
  await store.delete(options.key).catch(() => {});
}
